import { useState } from 'react';

interface TreeNode {
  id: string;
  children?: TreeNode[];
}

export const useTreeState = <T extends TreeNode, TId extends string = string>(
  onNodeSelect?: (id: TId) => void
) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const findParentNodes = (nodeId: string, nodes: T[]): string[] => {
    const parentNodes: string[] = [];

    const findParent = (id: string, currentNodes: T[]) => {
      for (const node of currentNodes) {
        if (node.id === id) {
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findParent(id, node.children as T[])) {
            parentNodes.push(node.id);
            return true;
          }
        }
      }
      return false;
    };

    findParent(nodeId, nodes);
    return parentNodes;
  };

  const handleSelect = async (id: string, nodes: T[]) => {
    setSelectedId(id);
    const parentNodes = findParentNodes(id, nodes);
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      parentNodes.forEach((nodeId) => newSet.add(nodeId));
      return newSet;
    });
    if (onNodeSelect) {
      onNodeSelect(id as TId);
    }
  };

  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const isExpanded = (nodeId: string) => expandedNodes.has(nodeId);

  return {
    selectedId,
    setSelectedId,
    handleSelect,
    handleToggleExpand,
    isExpanded,
  };
};
